import type { Intersection } from 'utility-types'

import { assert } from '../../../../../../utils'

import { ConnectwareError, ConnectwareErrorType, type CybusCoreContainer, type CybusServiceContainer } from '../../../../../../domain'

import type { PageSubscriptionsTypes, SingleSubscriptionsTypes, SubscriptionFilterArgs } from '../../../../../../application'

import { type BackendDefinition, type BackendHttpMethod, type BackendPath, mapToStatusType } from '../../../../../Connectware'
import type { HttpRequestArgs } from '../../../../Base'
import { BaseSubscriptionStrategy } from '../Base'
import { mapDockerContainerState } from './Docker'

type RequestConfig<Path extends BackendPath> = Pick<HttpRequestArgs<Path, BackendHttpMethod<Path>, never>, 'path' | 'method' | 'capability'>

type ListContainersRequestConfig = RequestConfig<'/api/containers' | '/api/core-containers'>
type InspectContainerRequestConfig = RequestConfig<'/api/containers/+/inspect' | '/api/core-containers/+/inspect'>
type OrchestratorRequestConfig = RequestConfig<'/api/containers/orchestrator' | '/api/core-containers/orchestrator'>

export type CommonContainer = Pick<Intersection<CybusServiceContainer, CybusCoreContainer>, 'id' | 'status' | 'lastUpdate' | 'created'>

export abstract class ContainerStrategy<
    P extends keyof PageSubscriptionsTypes,
    S extends keyof SingleSubscriptionsTypes,
    IdMetadata
> extends BaseSubscriptionStrategy<P, S> {
    /** Cache of the orchestrator */
    private isDocker: Promise<boolean> | null = null

    /**
     * Request configuration to inspect a container
     * If left null, it will cause the `retrieveAll` function to yield a not implemented error
     *
     * @see {ContainerStrategy.retrieveAll}
     */
    protected abstract readonly containerMetadataConfig: InspectContainerRequestConfig | null

    /**
     * Request configuration to fetch the containers orchestrator
     * If left null, it will cause the `retrieveAll` function to yield a not implemented error
     *
     * @see {ContainerStrategy.retrieveAll}
     */
    protected abstract readonly containersOrchestratorConfig: OrchestratorRequestConfig | null

    /**
     * Request configuration to fetch all containers
     * If left null, it will cause the `retrieveAll` function to yield a not implemented error
     *
     * @see {ContainerStrategy.retrieveAll}
     */
    protected abstract readonly containersConfig: ListContainersRequestConfig | null

    /**
     * Request configuration to inspect a container
     * If left null, it will cause the `retrieveOne` function to yield a not implemented error
     *
     * @see {ContainerStrategy.retrieveOne}
     */
    protected abstract readonly singleContainerConfig: InspectContainerRequestConfig | null

    /**
     * The mapper that maps the response of single retrieval
     * If left null, it will cause the `retrieveOne` function to yield a not implemented error
     *
     * @see {ContainerStrategy.retrieveOne}
     */
    protected abstract readonly mapSingleRetrieval:
        | ((id: string, data: BackendDefinition<'container-manager', 'ContainerResponse'>) => SingleSubscriptionsTypes[S])
        | null

    /**
     * With the given id and filters, generates id aux data
     * If left null, it will cause the `retrieveAll` function to yield a not implemented error
     * If it returns null, this particular container should be ignored
     *
     * @see {ContainerStrategy.retrieveAll}
     */
    protected abstract readonly mapIdAux: ((id: string, filter: SubscriptionFilterArgs) => IdMetadata | null) | null

    /**
     * With the given base values, generated and fetched metadata, map out a container in a list
     * If left null, it will cause the `retrieveAll` function to yield a not implemented error
     *
     * @see {ContainerStrategy.retrieveAll}
     */
    protected abstract readonly mapListContainer:
        | ((base: CommonContainer, idMetadata: IdMetadata, metadata: BackendDefinition<'container-manager', 'ContainerResponse'>) => PageSubscriptionsTypes[P])
        | null

    override retrieveAll (filter: SubscriptionFilterArgs): Promise<PageSubscriptionsTypes[P][]> {
        const { mapIdAux, mapListContainer, containersOrchestratorConfig, containersConfig, containerMetadataConfig } = this

        if (!mapIdAux || !mapListContainer || !containersOrchestratorConfig || !containersConfig || !containerMetadataConfig) {
            /** Not operating in fetch all mode */
            return super.retrieveAll(filter)
        }

        /** Lazily fetch and cache whether the orchestrator is Docker */
        this.isDocker =
            this.isDocker ??
            this.request<OrchestratorRequestConfig['path'], OrchestratorRequestConfig['method'], boolean>({
                ...containersOrchestratorConfig,
                authenticate: true,
                handlers: {
                    200: (response) => response.getJson().then((data) => data.orchestrator === 'Docker'),
                    503: ContainerStrategy.createServerErrorHandler('Could not fetch container metadata'),
                },
            })

        return this.request<
            ListContainersRequestConfig['path'],
            ListContainersRequestConfig['method'],
            BackendDefinition<'container-manager', 'ContainersResponse'>
        >({ ...containersConfig, authenticate: true, handlers: { 200: ContainerStrategy.createJsonHandler() } })
            .then((containers) =>
                Promise.all(
                    containers.map(async ({ Names, Names: [firstName], State }) => {
                        assert(firstName, new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Names in container not in expected format', { Names }))

                        // Name starts with a slash, this removes it
                        const id = firstName.slice(1)
                        const idAux = mapIdAux(id, filter)

                        if (idAux === null) {
                            // Can give up on this container as it is not relevant for this search
                            return null
                        }

                        const [inspectionMetadata, isDocker] = await Promise.all([
                            this.request<
                                InspectContainerRequestConfig['path'],
                                InspectContainerRequestConfig['method'],
                                BackendDefinition<'container-manager', 'ContainerResponse'>
                            >({
                                ...containerMetadataConfig,
                                pathParams: [id],
                                authenticate: true,
                                handlers: {
                                    200: ContainerStrategy.createJsonHandler(),
                                    404: ContainerStrategy.createNotFoundErrorHandler(containerMetadataConfig.path, id),
                                },
                            }),
                            this.isDocker,
                        ])

                        return mapListContainer(
                            {
                                id,
                                created: inspectionMetadata.Created ? new Date(inspectionMetadata.Created) : null,
                                ...(!isDocker
                                    ? { status: mapToStatusType(State?.toLowerCase()), lastUpdate: null }
                                    : mapDockerContainerState((inspectionMetadata as BackendDefinition<'container-manager', 'DockerContainerResponse'>).State)),
                            },
                            idAux,
                            inspectionMetadata
                        )
                    })
                )
            )
            .then((containers: (PageSubscriptionsTypes[P] | null)[]) => containers.filter((c): c is PageSubscriptionsTypes[P] => c !== null))
    }

    override retrieveOne (id: string): ReturnType<BaseSubscriptionStrategy<P, S>['retrieveOne']> {
        const { mapSingleRetrieval, singleContainerConfig } = this

        if (!mapSingleRetrieval || !singleContainerConfig) {
            /** Not operating in fetch single mode */
            return super.retrieveOne(id)
        }

        return this.request<InspectContainerRequestConfig['path'], InspectContainerRequestConfig['method'], ReturnType<typeof mapSingleRetrieval>>({
            ...singleContainerConfig,
            pathParams: [id],
            authenticate: true,
            handlers: {
                200: (response) => response.getJson().then((data) => mapSingleRetrieval(id, data)),
                404: ContainerStrategy.createNotFoundErrorHandler(singleContainerConfig.path, id),
            },
        })
    }
}
